import React, { ComponentType } from 'react'
import { Button } from '@/components/ui/button'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridItemChangeEvent,
  GridToolbar,
} from '@progress/kendo-react-grid'
import { LeadDetailsDto } from 'api/Leads/Types/LeadDetailsDto'
import { LeadNoteDto } from 'api/Leads/Types/LeadNoteDto'
import { Loading } from 'components/Loading'
import { NotesCommandCell } from 'components/Note/NotesCommandCell'
import { useNoteState } from 'components/Note/noteState'
import { NoteValue } from 'components/Note/types'
import { useApiClient } from 'utils/api/ApiClient'
import { LeadsApiClient } from 'utils/api/leadsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { formatLocalDateUTC } from 'utils/date'

import { LeadNavigation } from '../Navigation/LeadNavigation'
import { LeadTab } from './LeadSlug'

interface LeadDetailsNotesProps {
  leadNotes: LeadNoteDto[]
  onItemChange(event: GridItemChangeEvent): void
  onAddNew(): void
  actionCell: ComponentType<GridCellProps>
}

const LeadDetailsNotesUI: React.FC<LeadDetailsNotesProps> = (props) => {
  const { leadNotes, onAddNew, onItemChange, actionCell: ActionCell } = props

  return (
    <Grid data={leadNotes} onItemChange={onItemChange} editField='inEdit'>
      <GridToolbar>
        <Button size='xs' title='Add new' onClick={onAddNew}>
          Add new
        </Button>
      </GridToolbar>
      <Column field='description' title='Description' />
      <Column
        field='createdDate'
        cell={(gridCellProps: GridCellProps) => (
          <td>{formatLocalDateUTC(gridCellProps.dataItem.createdDate, true)}</td>
        )}
        title='Created Date'
      />
      <Column
        field='createdDate'
        cell={(gridCellProps: GridCellProps) => (
          <td>{formatLocalDateUTC(gridCellProps.dataItem.updatedDate, true)}</td>
        )}
        title='Updated Date'
      />
      <Column field='updatedBy.name' title='Updated By' editable={false} />
      <Column cell={ActionCell} width='140px' />
    </Grid>
  )
}

interface OuterProps {
  data: LeadDetailsDto
}

const LeadDetailsNotesPage: React.FC<OuterProps> = (props) => {
  const leadsApiClient = useApiClient(LeadsApiClient)
  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    return leadsApiClient.getLeadNotes(props.data.id)
  }, [])
  const { data = [], isLoading } = useFetchOne(callback)

  const handleAdd = async (note: NoteValue<LeadNoteDto>) => {
    if (props?.data?.id && note?.description) {
      await leadsApiClient.addLeadNote({
        leadId: props.data.id,
        description: note.description,
      })
    }
  }

  const handleUpdate = async (note: NoteValue<LeadNoteDto>) => {
    await leadsApiClient.updateLeadNote({
      id: note.id as string,
      description: note.description,
    })
  }

  const handleRemove = async (note: NoteValue<LeadNoteDto>) => {
    if (note?.id) {
      await leadsApiClient.removeLeadNote(note.id)
    }
  }

  const handleAddNew = () => {
    addNew({
      id: null,
      lead: null,
      description: null,
      createdDate: '',
      updatedDate: '',
      updatedBy: null,
    })
  }

  const handleEdit = (note: NoteValue<LeadNoteDto>) => {
    setEditNote(note)
  }

  const { notes, setNotes, setEditNote, addNew, ...noteState } = useNoteState({
    onAdd: handleAdd,
    onUpdate: handleUpdate,
    onRemove: handleRemove,
    onEdit: handleEdit,
    notes: data,
    forceRefresh,
  })

  const ActionCell = NotesCommandCell(noteState)

  return (
    <>
      <LeadNavigation data={props.data} isEdit={false} tab={LeadTab.NOTES} />
      <Loading isLoading={isLoading}>
        <LeadDetailsNotesUI
          leadNotes={notes}
          onItemChange={noteState.itemChange}
          onAddNew={handleAddNew}
          actionCell={ActionCell}
        />
      </Loading>
    </>
  )
}

export default LeadDetailsNotesPage
